type StyleValue = string | number;
type StyleVars = Record<string, StyleValue>;
type StyleProps = Record<string, StyleValue>;

class StyleTxn {
  private pendingVars = new Map<Element, StyleVars>();
  private pendingProps = new Map<Element, StyleProps>();
  private isOpen = false;

  public begin(): void {
    if (this.isOpen) return;
    this.isOpen = true;
  }

  public setVars(el: Element, vars: StyleVars): void {
    if (!this.isOpen) {
      console.warn("StyleTxn: call begin() first to set custom properties.");
      return;
    }
    const currentVars = this.pendingVars.get(el) ?? {};
    for (const [k, v] of Object.entries(vars)) {
      if (currentVars[k] === v) continue;
      currentVars[k] = v;
    }
    this.pendingVars.set(el, currentVars);
  }

  public setProps(el: Element, props: StyleProps): void {
    if (!this.isOpen) {
      console.warn("StyleTxn: call begin() first to set standard properties.");
      return;
    }
    const currentProps = this.pendingProps.get(el) ?? {};
    for (const [k, v] of Object.entries(props)) {
      if (currentProps[k] === v) continue;
      currentProps[k] = v;
    }

    this.pendingProps.set(el, currentProps);
  }

  public run(fn: () => void): void {
    const alreadyOpen = this.isOpen;
    if (!alreadyOpen) {
      this.begin();
    }
    try {
      fn();
      if (!alreadyOpen) {
        this.commit();
      }
    } catch (error) {
      if (!alreadyOpen) {
        this.cancel();
      }
      throw error;
    }
  }

  public commit(): void {
    if (!this.isOpen) return;
    this.isOpen = false;

    for (const [el, vars] of this.pendingVars) {
      const style = (el as HTMLElement).style;
      for (const [k, v] of Object.entries(vars)) {
        style.setProperty(k, String(v));
      }
    }
    this.pendingVars.clear();

    for (const [el, props] of this.pendingProps) {
      const style = (el as HTMLElement).style;
      for (const [k, v] of Object.entries(props)) {
        (style as any)[k] = String(v);
      }
    }
    this.pendingProps.clear();
  }

  public cancel(): void {
    this.pendingVars.clear();
    this.pendingProps.clear();
    this.isOpen = false;
  }
}

export const styleTxn = new StyleTxn();
